import {
    IDrivingStore,
    IDrivingVehicle
} from 'components/driving/driving-interface'
import ModalComponent from 'components/main/modal'
import { initValues, PostmediaStorage } from 'constants/enums'
import { ApiService } from 'services/api/api.service'
import { SessionStore } from 'services/store'
import { isArrayEqual } from 'utils/array'
import { logger } from 'utils/system/logger'
import { AbstractCard } from './abstract-card'
import { CompareEventType, MAX_VEHICLES } from './car-comparison/constants'
import { MyPicks } from './car-comparison/my-picks'
import { ModelCard } from './model-card/model-card'
import { TrimSelectorModel } from './trim-selector/trim-selector-model'
import { TrimSelectorComparisonResult } from './trim-selector/trim-selector-comparison-result'
import { VehicleFinderComponent } from './vehicle-finder/vehicle-finder.component'

/**
 * Driving car comparison session store controller
 */
export class CarComparisonStore {
    public trimSelectorModelModal: TrimSelectorModel
    public trimSelectorMyPicksModal: TrimSelectorComparisonResult
    public trimSelectorVehicleModal: TrimSelectorComparisonResult
    public limitExceededModal: ModalComponent
    public noMorePickModal: ModalComponent
    public modelCards: ModelCard[] = []
    public lastActiveCard: AbstractCard
    public vehicleFinder: VehicleFinderComponent
    public myPicks: MyPicks

    /**
     * Singleton pattern
     */
    private static _instance: CarComparisonStore
    public static getInstance(): CarComparisonStore {
        return this._instance || (this._instance = new this())
    }

    /**
     * Add vehicle to the store
     *
     * @param {number} id Vehicle to add
     * @param {IDrivingModel} model Vehicle model
     */
    public async add(id: number): Promise<void> {
        let vehicles = this.get()

        // Exceeded the limit. Send event and do nothing.
        if (vehicles.length >= MAX_VEHICLES) {
            this.update(
                vehicles,
                CompareEventType.DRIVING_COMPARE_ADD_OUT_OF_RANGE,
                undefined
            )
            return
        }

        // Existing vehicle. Do nothing.
        const exist = vehicles.reduce((indicator: boolean, vehicle) => {
            if (vehicle.id === id) {
                return true
            }
            return indicator
        }, false)
        if (exist) {
            return
        }

        // Store loading indicator and Send pre-event
        vehicles.push({
            id,
            isLoading: true
        })
        this.update(vehicles, CompareEventType.DRIVING_COMPARE_ADD_INIT, id)

        await new ApiService()
            .fetchDrivingVehicle(id)
            .then(async (vehicle: IDrivingVehicle) => {
                vehicles = this.get()
                // Update the vehicle
                vehicles = vehicles.map((item) => {
                    if (item.id === id) {
                        return {
                            ...item,
                            isLoading: false,
                            item: vehicle
                        }
                    }
                    return item
                })

                // Store
                this.update(
                    vehicles,
                    CompareEventType.DRIVING_COMPARE_ADD_SUCCESS,
                    vehicle
                )

                // Sponsored Vehicle
                await this.loadSponsoredVehicle(vehicles)
            })
            .catch((err) => {
                logger('error', `Vehicle API: ${err}`)
                vehicles = this.get()
                this.update(
                    vehicles.filter((vehicle) => vehicle.id !== id),
                    CompareEventType.DRIVING_COMPARE_ADD_FAILED,
                    id
                )
            })
    }

    /**
     * Remove vehicle by its id
     *
     * @param {number} id Vehicle to remove
     */
    public remove(id: number): void {
        // Remove
        const vehicles = this.get().filter((vehicle) => vehicle.id !== id)
        this.update(vehicles, CompareEventType.DRIVING_COMPARE_REMOVE, id)

        // Sponsored Vehicle
        void this.loadSponsoredVehicle(vehicles)
    }

    /**
     * Get stored vehicles
     *
     * @return {IDrivingStore<IDrivingVehicle>[]}
     */
    public get(): IDrivingStore<IDrivingVehicle>[] {
        return SessionStore.getInstance().get(PostmediaStorage.CAR_COMPARISON)
    }

    /**
     * Store data and send event
     *
     * @param {IDrivingStore<IDrivingVehicle>} vehicles
     * @param {string} event
     */
    private update(
        vehicles: IDrivingStore<IDrivingVehicle>[],
        event: string,
        detail: unknown
    ): void {
        // Store
        SessionStore.getInstance().set(
            PostmediaStorage.CAR_COMPARISON,
            vehicles
        )

        // Send event
        document.dispatchEvent(new CustomEvent(event, { detail }))
    }

    public registerModelCard(component: ModelCard): void {
        this.modelCards.push(component)
    }

    public updateModelCardsState(): void {
        this.modelCards.forEach((card) => card.setCardState())
    }

    public saveLastActiveCard(component: AbstractCard): void {
        this.lastActiveCard = component
    }

    /**
     * Get trim selector modal
     */
    public getTrimSelectorModel(): TrimSelectorModel {
        return (
            this.trimSelectorModelModal ||
            (this.trimSelectorModelModal = new TrimSelectorModel(
                'trim-selector-modal'
            ))
        )
    }

    public getTrimSelectorMyPicks(): TrimSelectorComparisonResult {
        return (
            this.trimSelectorMyPicksModal ||
            (this.trimSelectorMyPicksModal = new TrimSelectorComparisonResult(
                'trim-selector-modal'
            ))
        )
    }

    /**
     * Get trim selector modal for comparisoun result page
     */
    public getTrimSelectorVehicle(): TrimSelectorComparisonResult {
        return (
            this.trimSelectorVehicleModal ||
            (this.trimSelectorVehicleModal = new TrimSelectorComparisonResult(
                'trim-selector-modal'
            ))
        )
    }

    public getLimitExceededModal(): ModalComponent {
        return (
            this.limitExceededModal ||
            (this.limitExceededModal = new ModalComponent(
                undefined,
                'limit-exceeded-modal'
            ))
        )
    }

    public getNoMorePickModal(): ModalComponent {
        return (
            this.noMorePickModal ||
            (this.noMorePickModal = new ModalComponent(
                undefined,
                'no-more-pick-modal'
            ))
        )
    }

    /**
     * Get the sponsored vehicle from API
     *
     * @param {IDrivingStore<IDrivingVehicle>[]} vehicles
     * @return {Promise<void>}
     */
    private async loadSponsoredVehicle(
        vehicles: IDrivingStore<IDrivingVehicle>[]
    ): Promise<void> {
        // Reset if vehicle is empty
        if (!vehicles.length) {
            SessionStore.getInstance().set(
                PostmediaStorage.SPONSORED_VEHICLE,
                initValues[PostmediaStorage.SPONSORED_VEHICLE]
            )
            // do not render sponsored vehicle if my picks is not on the page
            if (this.myPicks) {
                this.myPicks.renderSponsoredVehicle()
            }
            return
        }

        const ids = vehicles
            .map((item) => (item.item ? item.item.id : 0))
            .filter((value) => value)
        const bodystyles = vehicles
            .map((item) => (item.item ? item.item.bodystyle : ''))
            .filter((value) => value)
        const msrps = vehicles
            .map((item) => (item.item ? item.item.msrp : 0))
            .filter((value) => value)

        await new ApiService()
            .fetchDrivingSponsoredVehicle({
                vehicle_ids: ids,
                bodystyles,
                msrps
            })
            .then((vehicle: IDrivingVehicle) => {
                SessionStore.getInstance().set(
                    PostmediaStorage.SPONSORED_VEHICLE,
                    { ids, item: vehicle }
                )
                this.myPicks.renderSponsoredVehicle(vehicle)
            })
            .catch((e) => logger('error', e))
    }
    /**
     * Init sponsored vehicle
     * When MyPicks and Sponsored are matched, render the section
     * When they are not matched, re-load sponsored
     */
    public initSponsoredVehicle(): void {
        const vehicles = this.get()
        const vehicleIds = vehicles.map((vehicle) => vehicle.id)
        const sponsored = SessionStore.getInstance().get(
            PostmediaStorage.SPONSORED_VEHICLE
        )
        if (isArrayEqual(vehicleIds, sponsored.ids)) {
            this.myPicks.renderSponsoredVehicle(sponsored.item)
            return
        }
        void this.loadSponsoredVehicle(vehicles)
    }
}
